import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { PanelLeft } from 'lucide-react';

import { Button } from './button';
import { Separator } from './separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';
import { cn } from '../../utils/index';

const SIDEBAR_WIDTH = '240px';
const SIDEBAR_WIDTH_ICON = '48px';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';

// --- Context ---

type SidebarContextValue = {
    open: boolean;
    setOpen: (open: boolean) => void;
    toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContextValue | null>(null);

const useSidebar = (): SidebarContextValue => {
    const context = React.useContext(SidebarContext);
    if (!context) {
        throw new Error('useSidebar must be used within a SidebarProvider');
    }
    return context;
};

// --- Provider ---

interface SidebarProviderProps extends React.ComponentPropsWithoutRef<'div'> {
    defaultOpen?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

const SidebarProvider = React.forwardRef<HTMLDivElement, SidebarProviderProps>(
    ({ defaultOpen = true, open: openProp, onOpenChange, className, style, children, ...props }, ref) => {
        const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
        const open = openProp ?? internalOpen;
        const setOpen = React.useCallback(
            (value: boolean) => {
                onOpenChange ? onOpenChange(value) : setInternalOpen(value);
            },
            [onOpenChange]
        );
        const openRef = React.useRef(open);
        openRef.current = open;
        const toggleSidebar = React.useCallback(() => setOpen(!openRef.current), [setOpen]);

        React.useEffect(() => {
            const handleKeyDown = (e: KeyboardEvent) => {
                if (e.key === SIDEBAR_KEYBOARD_SHORTCUT && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    toggleSidebar();
                }
            };
            window.addEventListener('keydown', handleKeyDown);
            return () => window.removeEventListener('keydown', handleKeyDown);
        }, [toggleSidebar]);

        const contextValue = React.useMemo<SidebarContextValue>(
            () => ({ open, setOpen, toggleSidebar }),
            [open, setOpen, toggleSidebar]
        );

        return (
            <SidebarContext.Provider value={contextValue}>
                <TooltipProvider delayDuration={0}>
                    <div
                        ref={ref}
                        data-sidebar="provider"
                        style={
                            {
                                '--sidebar-width': SIDEBAR_WIDTH,
                                '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
                                ...style,
                            } as React.CSSProperties
                        }
                        className={cn('flex min-h-svh w-full', className)}
                        {...props}
                    >
                        {children}
                    </div>
                </TooltipProvider>
            </SidebarContext.Provider>
        );
    }
);
SidebarProvider.displayName = 'SidebarProvider';

// --- Sidebar ---

interface SidebarProps extends React.ComponentPropsWithoutRef<'div'> {
    collapsible?: 'icon' | 'offcanvas' | 'none';
}

const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(
    ({ collapsible = 'icon', className, children, ...props }, ref) => {
        const { open } = useSidebar();

        if (collapsible === 'none') {
            return (
                <div
                    ref={ref}
                    className={cn('flex h-full w-[--sidebar-width] flex-col bg-sidebar text-sidebar-text', className)}
                    {...props}
                >
                    {children}
                </div>
            );
        }

        return (
            <div
                ref={ref}
                data-state={open ? 'expanded' : 'collapsed'}
                data-collapsible={collapsible}
                className={cn('group/sidebar relative flex h-full flex-col text-sidebar-text', className)}
            >
                {/* Width placeholder for layout */}
                <div
                    className={cn(
                        'relative w-[--sidebar-width] bg-transparent transition-[width] duration-200 ease-linear',
                        'group-data-[collapsible=offcanvas]/sidebar:w-0',
                        'group-data-[state=collapsed]/sidebar:w-[--sidebar-width-icon]'
                    )}
                />
                {/* Actual sidebar content */}
                <div
                    className={cn(
                        'fixed inset-y-0 left-0 z-10 flex h-svh w-[--sidebar-width] flex-col bg-sidebar border-r border-sidebar-border transition-[width] duration-200 ease-linear',
                        'group-data-[collapsible=offcanvas]/sidebar:left-[calc(var(--sidebar-width)*-1)]',
                        'group-data-[state=collapsed]/sidebar:w-[--sidebar-width-icon]',
                        'group-data-[state=collapsed]/sidebar:overflow-hidden'
                    )}
                    {...props}
                >
                    {children}
                </div>
            </div>
        );
    }
);
Sidebar.displayName = 'Sidebar';

// --- Layout Sections ---

const SidebarHeader = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
    ({ className, ...props }, ref) => (
        <div ref={ref} data-sidebar="header" className={cn('flex flex-col gap-2 p-2', className)} {...props} />
    )
);
SidebarHeader.displayName = 'SidebarHeader';

const SidebarContent = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            data-sidebar="content"
            className={cn('flex min-h-0 flex-1 flex-col gap-2 overflow-auto p-2', className)}
            {...props}
        />
    )
);
SidebarContent.displayName = 'SidebarContent';

const SidebarFooter = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
    ({ className, ...props }, ref) => (
        <div ref={ref} data-sidebar="footer" className={cn('flex flex-col gap-2 p-2', className)} {...props} />
    )
);
SidebarFooter.displayName = 'SidebarFooter';

// --- Group ---

const SidebarGroup = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            data-sidebar="group"
            className={cn('relative flex w-full min-w-0 flex-col p-2', className)}
            {...props}
        />
    )
);
SidebarGroup.displayName = 'SidebarGroup';

const SidebarGroupLabel = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            data-sidebar="group-label"
            className={cn(
                'flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-text/70 outline-none',
                'group-data-[state=collapsed]/sidebar:hidden',
                className
            )}
            {...props}
        />
    )
);
SidebarGroupLabel.displayName = 'SidebarGroupLabel';

// --- Menu ---

const SidebarMenu = React.forwardRef<HTMLUListElement, React.ComponentPropsWithoutRef<'ul'>>(
    ({ className, ...props }, ref) => (
        <ul ref={ref} data-sidebar="menu" className={cn('flex w-full min-w-0 flex-col gap-1', className)} {...props} />
    )
);
SidebarMenu.displayName = 'SidebarMenu';

const SidebarMenuItem = React.forwardRef<HTMLLIElement, React.ComponentPropsWithoutRef<'li'>>(
    ({ className, ...props }, ref) => (
        <li ref={ref} data-sidebar="menu-item" className={cn('group/menu-item relative', className)} {...props} />
    )
);
SidebarMenuItem.displayName = 'SidebarMenuItem';

// --- Menu Button ---

interface SidebarMenuButtonProps extends React.ComponentPropsWithoutRef<'button'> {
    asChild?: boolean;
    isActive?: boolean;
    tooltip?: string | React.ComponentPropsWithoutRef<typeof TooltipContent>;
}

const SidebarMenuButton = React.forwardRef<HTMLButtonElement, SidebarMenuButtonProps>(
    ({ asChild = false, isActive = false, tooltip, className, children, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        const { open } = useSidebar();

        const button = (
            <Comp
                ref={ref}
                data-sidebar="menu-button"
                data-active={isActive}
                className={cn(
                    'flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none transition-colors',
                    'hover:bg-sidebar-hover',
                    'data-[active=true]:bg-sidebar-active-bg data-[active=true]:text-sidebar-text-active data-[active=true]:font-medium',
                    'group-data-[state=collapsed]/sidebar:justify-center group-data-[state=collapsed]/sidebar:px-0',
                    className
                )}
                {...props}
            >
                {children}
            </Comp>
        );

        if (!tooltip) return button;

        const tooltipProps = typeof tooltip === 'string' ? { children: tooltip } : tooltip;

        return (
            <Tooltip>
                <TooltipTrigger asChild>{button}</TooltipTrigger>
                <TooltipContent side="right" align="center" {...tooltipProps} />
            </Tooltip>
        );
    }
);
SidebarMenuButton.displayName = 'SidebarMenuButton';

// --- Sub Menu ---

const SidebarMenuSub = React.forwardRef<HTMLUListElement, React.ComponentPropsWithoutRef<'ul'>>(
    ({ className, ...props }, ref) => (
        <ul
            ref={ref}
            data-sidebar="menu-sub"
            className={cn(
                'ml-3.5 flex min-w-0 flex-col gap-1 border-l border-sidebar-border pl-2.5 py-0.5',
                'group-data-[state=collapsed]/sidebar:hidden',
                className
            )}
            {...props}
        />
    )
);
SidebarMenuSub.displayName = 'SidebarMenuSub';

const SidebarMenuSubItem = React.forwardRef<HTMLLIElement, React.ComponentPropsWithoutRef<'li'>>(
    ({ className, ...props }, ref) => <li ref={ref} className={cn('relative', className)} {...props} />
);
SidebarMenuSubItem.displayName = 'SidebarMenuSubItem';

interface SidebarMenuSubButtonProps extends React.ComponentPropsWithoutRef<'a'> {
    asChild?: boolean;
    isActive?: boolean;
}

const SidebarMenuSubButton = React.forwardRef<HTMLAnchorElement, SidebarMenuSubButtonProps>(
    ({ asChild = false, isActive = false, className, ...props }, ref) => {
        const Comp = asChild ? Slot : 'a';
        return (
            <Comp
                ref={ref}
                data-sidebar="menu-sub-button"
                data-active={isActive}
                className={cn(
                    'flex h-7 min-w-0 items-center gap-2 overflow-hidden rounded-md px-2 text-sm text-sidebar-text outline-none',
                    'hover:bg-sidebar-hover',
                    'data-[active=true]:text-sidebar-text-active data-[active=true]:font-medium',
                    className
                )}
                {...props}
            />
        );
    }
);
SidebarMenuSubButton.displayName = 'SidebarMenuSubButton';

// --- Trigger ---

const SidebarTrigger = React.forwardRef<HTMLButtonElement, React.ComponentPropsWithoutRef<typeof Button>>(
    ({ className, onClick, ...props }, ref) => {
        const { toggleSidebar } = useSidebar();
        return (
            <Button
                ref={ref}
                variant="ghost"
                size="icon"
                className={cn('h-7 w-7', className)}
                onClick={e => {
                    onClick?.(e);
                    toggleSidebar();
                }}
                {...props}
            >
                <PanelLeft className="h-4 w-4" />
                <span className="sr-only">Toggle Sidebar</span>
            </Button>
        );
    }
);
SidebarTrigger.displayName = 'SidebarTrigger';

// --- Inset ---

const SidebarInset = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'main'>>(
    ({ className, ...props }, ref) => (
        <main ref={ref} className={cn('relative flex min-h-svh flex-1 flex-col bg-background', className)} {...props} />
    )
);
SidebarInset.displayName = 'SidebarInset';

// --- Separator ---

const SidebarSeparator = React.forwardRef<
    React.ElementRef<typeof Separator>,
    React.ComponentPropsWithoutRef<typeof Separator>
>(({ className, ...props }, ref) => (
    <Separator ref={ref} className={cn('mx-2 w-auto bg-sidebar-border', className)} {...props} />
));
SidebarSeparator.displayName = 'SidebarSeparator';

// --- Exports ---

export {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarInset,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    SidebarProvider,
    SidebarSeparator,
    SidebarTrigger,
    useSidebar,
};
